import type { Dayjs } from 'dayjs';
import type { FormInstance } from 'antd';
import { Frequency, ALL_WEEKDAYS, type WeekdayStr } from 'rrule';
import type { RobotCalendarEvent } from '../components/RobotSchedule/RobotSchedule.types';
import type { RobotScheduleRoutine, RobotScheduleRoutineSelectOption } from './RobotScheduleRoutine.types';
import type { RobotScheduleRoute, RobotScheduleRouteSelectOption } from './RobotScheduleRoute.types';
import type { RobotScheduleMapGroup } from './RobotScheduleMap.types';
import type {
  CleaningModeEnum,
  CleaningSuctionEnum,
  Map,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import type { Optional } from 'lib/types/Optional';

export interface MachineDetailsRobotSchedulePeriod {
  startDate: Dayjs;
  endDate: Dayjs;
}

export interface UseMachineDetailsRobotScheduleStateParams {
  robot: Machine;
}

export interface HandleOpenCreateModalOptions {
  isScheduleCreateFullSizeOpen?: boolean;
  dateTime?: Dayjs | Date | null;
  hasEventOverlap?: boolean;
}

export enum ScheduleUpdateScope {
  ThisEventOnly = 'THIS_EVENT_ONLY',
  ThisAndFollowing = 'THIS_AND_FOLLOWING',
}

export interface MachineDetailsRobotScheduleContextValue {
  robot: Machine;
  period: MachineDetailsRobotSchedulePeriod;
  weekNumber: number;
  today: string;
  isB50: boolean;
  isRobotOnline: boolean;
  isRobotVersionSupported: boolean;
  isReadOnly: boolean;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isScheduleCreateFullSizeOpen: boolean;
  isDeleteModalOpen: boolean;
  isMapAssignmentModalOpen: boolean;
  isUpdateConfirmModalOpen: boolean;
  hasOverlap: boolean;
  selectedEvent: RobotCalendarEvent | null;
  selectedCreateDateTime: Dayjs | null;
  isDeleteScheduleLoading: Optional<boolean>;
  isSelectedEventRepeating: boolean;
  updateScope: ScheduleUpdateScope;
  deleteScope: ScheduleUpdateScope;
  handlePeriodChange: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  handlePreviousWeek: () => void;
  handleNextWeek: () => void;
  handleToday: () => void;
  handleOpenCreateModal: (options?: HandleOpenCreateModalOptions) => void;
  handleOpenEditModal: (event: RobotCalendarEvent) => void;
  handleOpenUpdateConfirmModal: () => void;
  handleCloseUpdateConfirmModal: () => void;
  handleUpdateScopeChange: (scope: ScheduleUpdateScope) => void;
  handleCloseModal: () => void;
  handleAfterCloseModal: () => void;
  handleOpenDeleteModal: () => void;
  handleCloseDeleteModal: () => void;
  handleDeleteScopeChange: (scope: ScheduleUpdateScope) => void;
  handleDelete: () => void;
  handleCreateScheduleClick: () => void;
  handleMapAssignmentAssigned: () => void;
  handleCloseMapAssignmentModal: () => void;
  isScheduleMapsLoading: boolean;
}

export type SelectedScheduleRouteRoutine =
  | { type: 'routine'; routine: RobotScheduleRoutine }
  | { type: 'route'; route: RobotScheduleRoute };

export interface ScheduleRouteRoutineSelectOption {
  value: string;
  searchLabel: string;
  routine?: RobotScheduleRoutine;
  route?: RobotScheduleRoute;
}

export interface GetRouteNamesForRoutineOptions {
  routine: RobotScheduleRoutine;
  routes: RobotScheduleRoute[];
}

export interface FormatRoutineSelectLabelOptions {
  routine: RobotScheduleRoutine;
  routes: RobotScheduleRoute[];
  unnamedRoutineFallback: string;
}

export interface GetSelectedRouteRoutineByIdOptions {
  id: string;
  routines: RobotScheduleRoutine[];
  routes: RobotScheduleRoute[];
  unnamedRoutineFallback: string;
}

export interface MapScheduleRoutinesToSelectOptionsParams {
  routines: RobotScheduleRoutine[];
  routes: RobotScheduleRoute[];
  unnamedRoutineFallback: string;
}

export interface SelectedScheduleMap {
  type: 'map';
  map: Map;
}

export interface ScheduleMapSelectOption {
  value: string;
  searchLabel: string;
  map: Map;
}

export const WEEKDAYS_OPTIONS: WeekdayStr[] = [...ALL_WEEKDAYS];

export const WEEKDAY_LABEL_KEYS: Record<WeekdayStr, string> = {
  MO: 'common.mon',
  TU: 'common.tue',
  WE: 'common.wed',
  TH: 'common.thu',
  FR: 'common.fri',
  SA: 'common.sat',
  SU: 'common.sun',
};

export const ONCE_FREQUENCY = 'ONCE';

export type CV50_FREQUENCY = Frequency | typeof ONCE_FREQUENCY;

export const CV50_FREQUENCY_OPTIONS: CV50_FREQUENCY[] = [ONCE_FREQUENCY, Frequency.WEEKLY];

export const CV50_FREQUENCY_OPTIONS_LABELS: Record<CV50_FREQUENCY, string> = {
  [ONCE_FREQUENCY]: 'common.once',
  [Frequency.WEEKLY]: 'common.weekly',
  [Frequency.YEARLY]: '',
  [Frequency.MONTHLY]: '',
  [Frequency.DAILY]: '',
  [Frequency.HOURLY]: '',
  [Frequency.MINUTELY]: '',
  [Frequency.SECONDLY]: '',
};

export const FREQUENCY_OPTIONS: { label: string; value: Frequency }[] = [
  { label: 'createSchedule.daily', value: Frequency.DAILY },
  { label: 'createSchedule.weekly', value: Frequency.WEEKLY },
  { label: 'createSchedule.monthly', value: Frequency.MONTHLY },
];

export const FREQUENCY_OPTIONS_LABELS: Record<Frequency, string> = {
  [Frequency.DAILY]: 'createSchedule.day',
  [Frequency.WEEKLY]: 'createSchedule.week',
  [Frequency.MONTHLY]: 'createSchedule.month',
  [Frequency.YEARLY]: '',
  [Frequency.HOURLY]: '',
  [Frequency.MINUTELY]: '',
  [Frequency.SECONDLY]: '',
};

export const FREQUENCY_OPTIONS_PLURAL_LABELS: Record<Frequency, string> = {
  [Frequency.DAILY]: 'createSchedule.days',
  [Frequency.WEEKLY]: 'createSchedule.weeks',
  [Frequency.MONTHLY]: 'createSchedule.months',
  [Frequency.YEARLY]: '',
  [Frequency.HOURLY]: '',
  [Frequency.MINUTELY]: '',
  [Frequency.SECONDLY]: '',
};

export interface ScheduleUpsertModalB50FormValues {
  scheduleName: string;
  scheduleRouteRoutine: string;
  scheduleDate: Dayjs;
  scheduleTime: Dayjs;
  scheduleEndDate?: Dayjs;
  scheduleFrequency?: Frequency | null;
  scheduleEvery?: number;
  weekdays?: WeekdayStr[];
  isRepeating: boolean;
}

export interface ScheduleUpsertModalCV50FormValues {
  scheduleName: string;
  scheduleMap: string;
  scheduleDate: Dayjs;
  scheduleTime: Dayjs;
  suctionMode: CleaningSuctionEnum;
  cleaningMode: CleaningModeEnum;
  cleaningZones: string[];
  scheduleFrequency: CV50_FREQUENCY;
  weekdays: WeekdayStr[];
}

export interface UseScheduleCreateModalParams {
  robot: Machine;
  isB50: boolean;
  onClose: () => void;
  isOpen: boolean;
  isEventOpen: boolean;
  selectedEvent: RobotCalendarEvent | null;
  selectedCreateDateTime: Dayjs | null;
  updateScope: ScheduleUpdateScope;
  handleOpenUpdateConfirmModal: () => void;
}

export interface UseScheduleUpsertModalB50Params {
  robot: Machine;
  onClose: () => void;
  isOpen: boolean;
  isEventOpen: boolean;
  selectedEvent: RobotCalendarEvent | null;
  selectedCreateDateTime: Dayjs | null;
  updateScope: ScheduleUpdateScope;
  handleOpenUpdateConfirmModal: () => void;
}

export interface UseScheduleUpsertModalCV50Params {
  robot: Machine;
  onClose: () => void;
  isOpen: boolean;
  isEventOpen: boolean;
  selectedEvent: RobotCalendarEvent | null;
  selectedCreateDateTime: Dayjs | null;
  updateScope: ScheduleUpdateScope;
  handleOpenUpdateConfirmModal: () => void;
}

export interface ScheduleUpdateConfirmModalState {
  handleSaveChanges: () => void;
}

export interface ScheduleUpsertModalB50ContextValue extends ScheduleUpdateConfirmModalState {
  form: FormInstance<ScheduleUpsertModalB50FormValues>;
  robot: Machine;
  isDropdownVisible: boolean;
  expectedCompletionTime: {
    finishCleaningTime: Dayjs;
    fullyChargedTime: Dayjs;
  };
  isRepeating: boolean;
  hasScheduleChanges: boolean;
  scheduleName?: string;
  scheduleRouteRoutine: SelectedScheduleRouteRoutine | undefined;
  scheduleRoutineOptions: RobotScheduleRoutineSelectOption[];
  scheduleRouteOptions: RobotScheduleRouteSelectOption[];
  isLoadingScheduleRouteRoutine: boolean;
  hasErrorScheduleName: boolean;
  hasErrorScheduleRouteRoutine: boolean;
  isEditingScheduleName: boolean;
  isScheduleNameInvalid: boolean;
  handleToggleEditingScheduleName: () => void;
  handleDropdownVisibleChange: (visible: boolean) => void;
  handleEditScheduleRouteRoutine: () => void;
  handleScheduleRouteRoutineChange: (selectedScheduleRouteRoutine: SelectedScheduleRouteRoutine | undefined) => void;
  handleRepeatingChange: (checked: boolean) => void;
  handleWeekdaysChange: (checked: boolean, tag: WeekdayStr) => void;
  handleFormValuesChange: (changedValues: ScheduleUpsertModalB50FormValues) => void;
  handleValidateFields: (fields?: string[]) => void;
  handleSubmit: (values: ScheduleUpsertModalB50FormValues) => void;
  handleCancel: () => void;
}

export interface ScheduleUpsertModalCV50ContextValue extends ScheduleUpdateConfirmModalState {
  form: FormInstance<ScheduleUpsertModalCV50FormValues>;
  robot: Machine;
  isDropdownVisible: boolean;
  expectedCompletionTime: {
    finishCleaningTime: Dayjs;
  };
  hasScheduleChanges: boolean;
  scheduleName?: string;
  scheduleMap: SelectedScheduleMap | undefined;
  scheduleMapGroups: RobotScheduleMapGroup[];
  isScheduleMapsLoading: boolean;
  hasErrorScheduleName: boolean;
  hasErrorScheduleMap: boolean;
  isEditingScheduleName: boolean;
  isScheduleNameInvalid: boolean;
  handleToggleEditingScheduleName: () => void;
  handleDropdownVisibleChange: (visible: boolean) => void;
  handleEditScheduleMap: () => void;
  handleScheduleMapChange: (selectedScheduleMap: SelectedScheduleMap | undefined) => void;
  handleWeekdaysChange: (checked: boolean, tag: WeekdayStr) => void;
  handleFrequencyChange: (checked: boolean, tag: CV50_FREQUENCY) => void;
  handleCleaningZonesChange: (nextCleaningZones: string[]) => void;
  handleFormValuesChange: (changedValues: ScheduleUpsertModalCV50FormValues) => void;
  handleValidateFields: (fields?: string[]) => void;
  handleSubmit: (values: ScheduleUpsertModalCV50FormValues) => void;
  handleCancel: () => void;
}

/** Fields shared by B50 and CV50 that the modal shell can consume without branching. */
export interface ScheduleCreateModalSharedContextValue extends ScheduleUpdateConfirmModalState {
  form: FormInstance;
  robot: Machine;
  hasScheduleChanges: boolean;
  handleFormValuesChange: (changedValues: ScheduleUpsertModalCV50FormValues | ScheduleUpsertModalB50FormValues) => void;
  handleValidateFields: (fields?: string[]) => void;
  handleSubmit: (values: ScheduleUpsertModalB50FormValues | ScheduleUpsertModalCV50FormValues) => void;
  handleCancel: () => void;
  scheduleName?: string;
  isEditingScheduleName: boolean;
  isScheduleNameInvalid: boolean;
  handleToggleEditingScheduleName: () => void;
}

export type ScheduleSyncStatus =
  | { kind: 'upToDate' }
  | { kind: 'minutesAgo'; minutes: number }
  | { kind: 'dateTime'; syncedAt: string };
